import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FlaskConical, Stethoscope } from "lucide-react";
import { AFFILIATE_URLS, trackAffiliateClick } from "../config/affiliate";

/**
 * Dual-path acquire section for /peptides/:slug.
 *
 * Compliance model:
 *  - LEFT (Tier 3, research-only material): affiliate handoff to Paramount
 *    Peptides — the correct destination for a laboratory researcher.
 *  - RIGHT (Tier 1/2, medically-supervised care): referral to MyDose.ai —
 *    the correct destination for anyone who wants a physician-supervised,
 *    503A-pharmacy-fulfilled protocol.
 *
 * Both paths are shown together so a visitor is NEVER led to interpret a
 * research-only material as an interchangeable substitute for a supervised
 * medical protocol. Rendered AFTER the educational Product Specs + Research
 * Summary so the regulatory context is absorbed first.
 */
export const PeptideAcquireSection = ({ product, slug }) => {
  if (!product) return null;

  const sourcePage = `/peptides/${slug}`;

  return (
    <section className="py-10 bg-[#fffaf5]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-5">
          <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-1">
            Two paths from here
          </h2>
          <p className="text-sm text-[#7a6b62] leading-relaxed">
            Choose the path that matches your use case. Both are transparently disclosed; both route you to a licensed third-party partner.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5" data-testid="peptide-acquire-dual">
          {/* PATH 1 — Research material (Paramount) */}
          <article
            className="bg-white border border-[#f0d8d4] rounded-2xl p-6 flex flex-col"
            data-testid="peptide-acquire-research"
          >
            <div className="flex items-start gap-3 mb-3">
              <FlaskConical className="w-5 h-5 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-[#b89a6d] uppercase tracking-wider">
                  Research use
                </p>
                <h3 className="text-lg font-bold text-[#3a2a26]">
                  Order the research material
                </h3>
              </div>
            </div>
            <p className="text-sm text-[#7a6b62] leading-relaxed mb-4 flex-1">
              Fulfilled by our verified-provider partner. By proceeding you acknowledge this is a research chemical for laboratory use only, not approved by the FDA, and not intended for human or veterinary use. See our{" "}
              <Link to="/editorial-standards" className="text-[#b89a6d] hover:text-[#a08560] underline">
                editorial standards
              </Link>{" "}
              and{" "}
              <Link to="/compliance" className="text-[#b89a6d] hover:text-[#a08560] underline">
                compliance framework
              </Link>
              .
            </p>
            <a
              href={product.buyLink}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              onClick={() =>
                trackAffiliateClick({
                  partner: "paramount",
                  product: product.name,
                  destination: product.buyLink,
                  sourcePage,
                })
              }
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-lg transition-colors"
              data-testid="peptide-buy-link"
            >
              Continue to Verified Provider <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-xs text-[#a09186] mt-3">
              Affiliate disclosure: PeptidesForMedSpas earns a commission from sales through this link. Does not affect price or editorial content.
            </p>
          </article>

          {/* PATH 2 — Supervised medical care (MyDose) */}
          <article
            className="bg-white border-2 border-[#e8c5b8] rounded-2xl p-6 flex flex-col"
            data-testid="peptide-acquire-medical"
          >
            <div className="flex items-start gap-3 mb-3">
              <Stethoscope className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Supervised medical care
                </p>
                <h3 className="text-lg font-bold text-[#3a2a26]">
                  Talk to a licensed provider
                </h3>
              </div>
            </div>
            <p className="text-sm text-[#7a6b62] leading-relaxed mb-4 flex-1">
              If you want a physician-supervised protocol dispensed by a U.S.-licensed 503A compounding pharmacy, our referral partner{" "}
              <a
                href={AFFILIATE_URLS.mydose}
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="text-emerald-700 hover:text-emerald-800 underline font-medium"
              >
                MyDose.ai
              </a>{" "}
              runs the intake, clinician review, and pharmacy fulfillment. HIPAA-compliant, audit trails, free to start. See our{" "}
              <Link to="/partners" className="text-[#b89a6d] hover:text-[#a08560] underline">
                partners page
              </Link>
              .
            </p>
            <a
              href={AFFILIATE_URLS.mydose}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              onClick={() =>
                trackAffiliateClick({
                  partner: "mydose",
                  product: product.name,
                  destination: AFFILIATE_URLS.mydose,
                  sourcePage,
                })
              }
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
              data-testid="peptide-mydose-link"
            >
              Explore Supervised Care via MyDose <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-xs text-[#a09186] mt-3">
              Referral disclosure: MyDose.ai is an educational referral partner. Not medical advice; only your licensed clinician can determine what is appropriate for you.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default PeptideAcquireSection;
